import s from './Step2.module.scss';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect } from 'react';
import { Step2Query, useStep2Mutation } from '../../api/createUsualAccountApi';
import { useAppDispatch, useAppSelector } from '@/shared/lib/hooks/redux';
import { Step2Data } from '../../model/types/step2';
import { step2Schema } from '../../lib/validationSchema/Step2Schema';
import { Button } from '@/shared/ui/Button/Button';
import { Input } from '@/shared/ui/Input/Input';
import { InputPassword } from '@/shared/ui/InputPassword/InputPassword';
import { InputDate } from '@/shared/ui/InputDate/InputDate';
import { setRegistrationData } from '@/features/CreateUsualAccountForm';
import { prevStep, resetCreateUsualAccountStepperData } from '@/entities/CreateUsualAccountStepper';
import { setErrorStatusModal, setSuccessStatusModal } from '@/entities/StatusModal';
import { resetRegistrationData } from '@/features/CreateUsualAccountForm';
import { useFormError } from '@/shared/lib/hooks/useFormError';
import { useScreenDetector } from '@/shared/lib/hooks/useScreenDetector';
import { PasswordRequirementsList } from '@/shared/ui/PasswordRequirementsList/PasswordRequirementsList';

interface Step2Props {
  onClose: () => void;
}

export const Step2 = (props: Step2Props) => {
  const { onClose } = props;
  const dispatch = useAppDispatch();
  const { isMobile } = useScreenDetector();
  const [triggerStep2, { isLoading, isSuccess, error }] = useStep2Mutation();

  const {
    registrationToken,
    data: { birthDate, nickname, vkLink, firstName, lastName, patronymic },
  } = useAppSelector((state) => state.createUsualAccount);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitted, touchedFields },
    setError,
  } = useForm<Step2Data>({
    mode: 'onTouched',
    resolver: yupResolver(step2Schema),
    criteriaMode: 'all',
    defaultValues: {
      birthDate: birthDate || '',
      nickname: nickname || '',
      vkLink: vkLink || '',
      password: '',
      confirmPassword: '',
    },
  });

  const onSubmit: SubmitHandler<Step2Data> = (data) => {
    triggerStep2({
      registrationToken,
      nickname: data.nickname,
      password: data.password,
      birthDate: data.birthDate || undefined,
      vkLink: data.vkLink || undefined,
    }).then(() => {
      dispatch(
        setRegistrationData({
          birthDate: data.birthDate,
          nickname: data.nickname,
          vkLink: data.vkLink,
        }),
      );
    });
  };

  useEffect(() => {
    if (isSuccess) {
      const fullName = patronymic ? `${lastName} ${firstName} ${patronymic}` : `${lastName} ${firstName}`;
      dispatch(
        setSuccessStatusModal({
          name: fullName.trim() || nickname,
        }),
      );
      dispatch(resetCreateUsualAccountStepperData());
      dispatch(resetRegistrationData());
      onClose();
    }
  }, [isSuccess]);

  const onError = () => {
    const fullName = patronymic ? `${lastName} ${firstName} ${patronymic}` : `${lastName} ${firstName}`;
    dispatch(
      setErrorStatusModal({
        name: fullName.trim() || nickname,
      }),
    );
  };

  useFormError<Step2Query>(error, setError, onError);

  const getErrorMessage = (errorField: typeof errors.password | typeof errors.confirmPassword) =>
    typeof errorField?.message === 'string' ? errorField.message : '';

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className={s.inputs}>
        <Controller
          name="birthDate"
          control={control}
          render={({ field: { value, onChange, onBlur, name } }) => (
            <InputDate
              value={value}
              onChange={onChange}
              label={'Дата рождения'}
              placeholder={'дд.мм.гггг'}
              error={errors.birthDate?.message}
              isSubmitted={isSubmitted}
              onBlur={onBlur}
              name={name}
              inputId={'player-birth-date'}
              autoComplete={'off'}
            />
          )}
        />
        <Controller
          name="nickname"
          control={control}
          render={({ field: { value, onChange, onBlur, name } }) => (
            <Input
              value={value}
              onChange={onChange}
              label={'Позывной*'}
              placeholder={'Введите ваш позывной'}
              error={errors.nickname?.message}
              isSubmitted={isSubmitted}
              onBlur={onBlur}
              name={name}
              inputId={'player-nickname'}
              autoComplete={'off'}
              tooltipValue={'При длиннее свыше 13 символов, позывной не полностью отобразится на нашивке персонажа.'}
              tooltipClassName={s.tooltip}
            />
          )}
        />
        <Controller
          name="vkLink"
          control={control}
          render={({ field: { value, onChange, onBlur, name } }) => (
            <Input
              value={value}
              onChange={onChange}
              label={'Ссылка на VK'}
              placeholder={'https://vk.ru/username'}
              error={errors.vkLink?.message}
              isSubmitted={isSubmitted}
              onBlur={onBlur}
              name={name}
              inputId={'player-vk-link'}
              autoComplete={'off'}
            />
          )}
        />
        <Controller
          name="password"
          control={control}
          render={({ field: { value, onChange, onBlur, name } }) => (
            <InputPassword
              value={value}
              onChange={onChange}
              label={'Пароль*'}
              placeholder={'Введите пароль'}
              error={getErrorMessage(errors.password)}
              onBlur={onBlur}
              name={name}
              inputId={'player-password'}
              autoComplete={'new-password'}
            />
          )}
        />
        <Controller
          name="confirmPassword"
          control={control}
          render={({ field: { value, onChange, onBlur, name } }) => (
            <InputPassword
              value={value}
              onChange={onChange}
              label={'Повторите пароль*'}
              placeholder={'Введите пароль'}
              error={getErrorMessage(errors.confirmPassword)}
              onBlur={onBlur}
              name={name}
              inputId={'player-password-confirm'}
              autoComplete={'new-password'}
            />
          )}
        />
      </div>
      <PasswordRequirementsList error={errors.password} isTouched={touchedFields.password} />
      <div className={s.actions}>
        <Button onClick={() => dispatch(prevStep())} variant={'outline'} className={s.prev} size={isMobile ? 's' : 'm'}>
          Назад
        </Button>
        <Button
          type={'submit'}
          className={s.next}
          disabled={isLoading}
          size={isMobile ? 's' : 'm'}
          onClick={handleSubmit(onSubmit)}
        >
          Зарегистрироваться
        </Button>
      </div>
    </form>
  );
};
